import { useMemo } from 'react';
import { startOfDay } from 'date-fns';

import type { Task } from '@/project-management/entities/task';
import type { Workstream } from '@/project-management/entities/workstream';
import { cls } from '@/utils/cls';

import { datePosition } from '../utils/date-position';

import { TimelineTask } from './timeline-task';

export type TimelineWorkstreamProps = {
  workstream: Pick<Workstream, 'id' | 'startDate' | 'endDate' | 'name'> & {
    tasks: Pick<Task, 'id' | 'title' | 'dueDate' | 'status' | 'priority'>[];
  };
  start: Date;
  end: Date;
  className?: string;
};

export function TimelineWorkstream({
  workstream,
  start,
  end,
  className,
}: TimelineWorkstreamProps) {
  const startPosition = datePosition(workstream.startDate, start, end);
  const endPosition = datePosition(workstream.endDate, start, end);

  const groupedTasks = useGroupTasksPerDay(workstream.tasks, workstream);

  return (
    <div className={cls('h-3 w-full', className)}>
      <div className="relative w-full">
        <div
          className="absolute inset-y-0 w-auto"
          style={{
            left: `${startPosition * 100}%`,
            right: `${(1 - endPosition) * 100}%`,
          }}
        >
          <div className="relative h-3 w-full rounded-md bg-gray-200"></div>
        </div>

        {groupedTasks.map(([date, tasks]) => {
          const position = datePosition(date, start, end);

          return (
            <TimelineTask
              key={date.getTime()}
              date={date}
              tasks={tasks}
              tooltipPosition={
                position > 0.8 || tasks.length > 3 ? 'left' : 'top'
              }
              className="absolute -top-0.5 size-4 -translate-x-1/2"
              style={{ left: `${position * 100}%` }}
            />
          );
        })}
      </div>
    </div>
  );
}

function useGroupTasksPerDay<
  T extends Pick<Task, 'dueDate'>,
  W extends Pick<Workstream, 'endDate'>,
>(tasks: T[], workstream: W): [Date, T[]][] {
  return useMemo(
    function () {
      const results = new Map<string, [Date, T[]]>();

      for (const task of tasks) {
        const date = task.dueDate ?? workstream.endDate;
        const day = startOfDay(date);
        const key = day.toISOString();

        const [_, existingTasks = []] = results.get(key) ?? [];
        const tasks = [...existingTasks, task];

        results.set(key, [day, tasks]);
      }

      return Array.from(results.values());
    },
    [tasks, workstream.endDate],
  );
}
